import { Capacitor } from '@capacitor/core';
import * as SentryCapacitor from '@sentry/capacitor';
import { init as electronRendererInit } from '@sentry/electron/renderer';
import * as SentryReact from '@sentry/react';
import { isElectron } from '@/utils/platformUtils';

type SentryInitOptions = Parameters<typeof SentryReact.init>[0];

function getSentryEnvironment(): string {
  if (isElectron()) {
    const platform = window.electronAPI?.platform ?? 'unknown';
    return `electron-${platform}`;
  }
  if (Capacitor.isNativePlatform()) {
    return `capacitor-${Capacitor.getPlatform()}`;
  }
  return 'pwa';
}

function buildIntegrations(includeReplay: boolean) {
  const integrations = [
    SentryReact.browserTracingIntegration(),
    SentryReact.consoleLoggingIntegration({ levels: ['log', 'warn', 'error'] }),
  ];
  if (includeReplay) {
    integrations.push(SentryReact.replayIntegration());
  }
  return integrations;
}

/**
 * Electron renderer init. @sentry/electron bundles @sentry/browser@10.50 while
 * @sentry/capacitor requires @sentry/react@10.69 — Integration types differ.
 * Apply React integrations inside the init callback (documented Sentry pattern).
 */
/**
 * Packaged Electron often runs on thin Windows POS hardware. Tracing every
 * span plus session replay competes with tap handling on 2-core iGPUs.
 * Keep crash/error events; sample performance; skip replay.
 */
const ELECTRON_TRACES_SAMPLE_RATE = 0.05;

function initElectronRendererSentry(): void {
  electronRendererInit(
    {
      tracesSampleRate: ELECTRON_TRACES_SAMPLE_RATE,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      enableLogs: false,
      sendDefaultPii: true,
    },
    (electronOptions) => {
      SentryReact.init({
        ...electronOptions,
        integrations: (defaults) => [...defaults, ...buildIntegrations(false)],
      });
    },
  );
}

function initCapacitorSentry(): void {
  const isNativeCapacitor = Capacitor.isNativePlatform();
  const options: SentryInitOptions = {
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: getSentryEnvironment(),
    release: import.meta.env.VITE_APP_VERSION,
    enableLogs: true,
    sendDefaultPii: true,
    tracesSampleRate: 1.0,
    integrations: buildIntegrations(!isNativeCapacitor),
    replaysSessionSampleRate: isNativeCapacitor ? 0 : 0.1,
    replaysOnErrorSampleRate: isNativeCapacitor ? 0 : 1.0,
  };
  SentryCapacitor.init(options, SentryReact.init);
}

function initPwaSentry(): void {
  const isNativeCapacitor = Capacitor.isNativePlatform();
  SentryReact.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: getSentryEnvironment(),
    release: import.meta.env.VITE_APP_VERSION,
    enableLogs: true,
    sendDefaultPii: true,
    tracesSampleRate: 1.0,
    integrations: buildIntegrations(!isNativeCapacitor),
    replaysSessionSampleRate: isNativeCapacitor ? 0 : 0.1,
    replaysOnErrorSampleRate: isNativeCapacitor ? 0 : 1.0,
  });
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn || import.meta.env.DEV) {
    console.log('Sentry not configured (dev or no DSN)');
    return;
  }

  if (isElectron()) {
    initElectronRendererSentry();
    console.log(`Sentry initialized (${getSentryEnvironment()} renderer)`);
    return;
  }

  if (Capacitor.isNativePlatform()) {
    initCapacitorSentry();
    console.log(`Sentry initialized (${getSentryEnvironment()})`);
    return;
  }

  initPwaSentry();
  console.log('Sentry initialized (pwa)');
}
